// Authentication service
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface User {
  id: number;
  email: string;
  full_name: string;
  user_type: 'internal' | 'client';
  role_id: number;
  role_name: string;
  client_id: number | null;
  avatar_url: string | null;
}

export interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

export interface AuthError {
  error: string;
}

// Store token in localStorage
export const setAuthToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
};

// Get token from localStorage
export const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Remove token from localStorage
export const removeAuthToken = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
};

// Store user data
export const setUserData = (user: User): void => {
  localStorage.setItem('user_data', JSON.stringify(user));
};

// Get user data
export const getUserData = (): User | null => {
  const userData = localStorage.getItem('user_data');
  return userData ? JSON.parse(userData) : null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Login function
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/login.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao fazer login');
  }

  // Store token and user data
  console.log('[Login] Saving token and user data to localStorage');
  console.log('[Login] Token:', data.token ? `${data.token.substring(0, 20)}...` : 'NULL');
  console.log('[Login] User:', data.user);
  setAuthToken(data.token);
  setUserData(data.user);
  console.log('[Login] Verification - Token saved:', getAuthToken() ? 'YES' : 'NO');
  console.log('[Login] Verification - User saved:', getUserData() ? 'YES' : 'NO');

  return data;
};

// Logout function
export const logout = async (): Promise<void> => {
  const token = getAuthToken();
  
  if (token) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Clear local storage regardless of API call result
  removeAuthToken();
};

// Forgot password function
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao solicitar recuperação de senha');
  }

  return data;
};

// Reset password function
export const resetPassword = async (token: string, password: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao redefinir senha');
  }

  return data;
};

// API fetch with authentication
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();
  
  console.log('[Auth] Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'NULL');

  if (!token) {
    throw new Error('Não autenticado');
  }

  // Detectar se o body é FormData - nesse caso, não adicionar Content-Type
  // O browser configura automaticamente como multipart/form-data com boundary
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    ...(options.headers as Record<string, string> || {}),
  };

  // Só adicionar Content-Type se não for FormData
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If token is expired, redirect to login
  if (response.status === 401) {
    removeAuthToken();
    window.location.href = '/area-cliente';
    throw new Error('Sessão expirada');
  }

  return response;
};
